import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { collab } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Search, Plus, Music2 } from "lucide-react";

export default function CollabAdd() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [name, setName] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [added, setAdded] = useState([]);
  const [queue, setQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(true);

  useEffect(() => {
    collab
      .info(token)
      .then((d) => {
        setInfo(d);
        const saved = localStorage.getItem("collab_name");
        if (saved) setName(saved);
      })
      .catch(() => setInfo({ error: true }));
  }, [token]);

  const refreshQueue = async () => {
    try {
      const d = await collab.songs(token);
      setQueue(d.songs || []);
    } catch (_) {}
    finally {
      setQueueLoading(false);
    }
  };

  useEffect(() => {
    if (!info || info.error) return;
    refreshQueue();
    const iv = setInterval(refreshQueue, 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info, token]);

  const doSearch = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const data = await collab.search(token, q);
      setResults(data.results || []);
    } catch (e) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const persistName = (v) => {
    setName(v);
    localStorage.setItem("collab_name", v);
  };

  const addSong = async (item) => {
    try {
      const song = await collab.add(token, item.video_id, name || "Guest");
      setAdded((a) => [song, ...a]);
      toast.success(`Added: ${song.title}`);
      refreshQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    }
  };

  const addFromInput = async () => {
    try {
      const song = await collab.add(token, q, name || "Guest");
      setAdded((a) => [song, ...a]);
      setQ("");
      setResults([]);
      toast.success(`Added: ${song.title}`);
      refreshQueue();
    } catch (e) {
      toast.error("Invalid YouTube URL");
    }
  };

  if (!info) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="mx-auto w-10 h-10 rounded-full border-2 border-fuchsia-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (info.error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="font-heading font-black text-3xl mb-2">Invalid invite</div>
          <div className="text-zinc-500">This link is broken or expired.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white grain relative overflow-hidden">
      <div
        className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(217,70,239,0.25), transparent 60%)",
          filter: "blur(80px)",
        }}
      />
      <div className="relative z-10 max-w-2xl mx-auto px-5 py-10">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-800 bg-zinc-950/50 mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-fuchsia-400 animate-pulse" />
            <span className="font-mono text-[10px] tracking-widest text-zinc-400">INVITED TO</span>
          </div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">{info.party_name}</h1>
          <div className="text-sm text-zinc-500 mt-1">
            Hosted by {info.host_name} · {queue.length} tracks in queue
          </div>
        </div>

        {/* Queue */}
        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 overflow-hidden mb-5">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Music2 className="w-4 h-4 text-fuchsia-400" />
              <span className="font-mono text-[10px] tracking-widest text-zinc-500">
                THE QUEUE
              </span>
            </div>
            <button
              onClick={refreshQueue}
              className="font-mono text-[9px] tracking-widest text-zinc-500 hover:text-white"
              data-testid="collab-refresh-queue"
            >
              REFRESH
            </button>
          </div>
          {queueLoading ? (
            <div className="p-6 text-center text-zinc-500 text-xs">Loading...</div>
          ) : queue.length === 0 ? (
            <div className="p-6 text-center text-zinc-500 text-sm">
              Empty. Be the first to add a banger.
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto divide-y divide-zinc-800/60">
              {queue.map((s, i) => (
                <div key={s.song_uid} className="flex items-center gap-3 px-3 py-2">
                  <span className="font-mono text-[10px] text-zinc-600 w-5 text-right">
                    {i + 1}
                  </span>
                  <img src={s.thumbnail} alt="" className="w-10 h-8 object-cover rounded" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white truncate leading-tight">{s.title}</div>
                    <div className="text-[10px] text-zinc-500 truncate">
                      {s.channel} · added by <span className="text-zinc-400">{s.added_by || "Host"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Name */}
        <div className="mb-5">
          <label className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1 block">YOUR NAME</label>
          <Input
            value={name}
            onChange={(e) => persistName(e.target.value)}
            placeholder="Alex from apartment 3B"
            className="bg-zinc-900 border-zinc-800 text-white"
            data-testid="collab-name-input"
          />
        </div>

        {/* Search */}
        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-4 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-zinc-500" />
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">FIND A BANGER</span>
          </div>
          <div className="flex gap-2">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search YouTube or paste link..."
              className="bg-zinc-900 border-zinc-800 text-white"
              data-testid="collab-search-input"
            />
            <Button
              onClick={doSearch}
              disabled={searching}
              className="bg-fuchsia-600 hover:bg-fuchsia-500"
              data-testid="collab-search-btn"
            >
              {searching ? "..." : "Search"}
            </Button>
            <Button
              onClick={addFromInput}
              variant="outline"
              className="border-zinc-700 text-white hover:bg-zinc-900"
              data-testid="collab-add-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {results.length > 0 && (
            <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
              {results.map((r) => (
                <div
                  key={r.video_id}
                  className="flex items-center gap-3 p-2 rounded-lg bg-zinc-900/60 hover:bg-zinc-900"
                >
                  <div className="relative shrink-0">
                    <img src={r.thumbnail} alt="" className="w-16 h-12 object-cover rounded" />
                    {r.duration_seconds ? (
                      <span
                        className="absolute bottom-0.5 right-0.5 px-1 py-px rounded font-mono text-[9px] leading-none text-white"
                        style={{ background: "rgba(0,0,0,0.75)" }}
                        data-testid={`collab-duration-${r.video_id}`}
                      >
                        {fmtCollabDuration(r.duration_seconds)}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{r.title}</div>
                    <div className="text-xs text-zinc-500 truncate">{r.channel}</div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => addSong(r)}
                    className="bg-fuchsia-600 hover:bg-fuchsia-500"
                    data-testid={`collab-add-result-${r.video_id}`}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {added.length > 0 && (
          <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Music2 className="w-4 h-4 text-cyan-400" />
              <span className="font-mono text-[10px] tracking-widest text-zinc-500">YOU ADDED</span>
            </div>
            <div className="space-y-2">
              {added.map((s) => (
                <div key={s.song_uid} className="flex items-center gap-3 text-sm">
                  <img src={s.thumbnail} alt="" className="w-10 h-8 object-cover rounded" />
                  <div className="truncate flex-1">{s.title}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function fmtCollabDuration(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}
